import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "reactstrap";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Wizard } from "react-use-wizard";
import { connect } from "react-redux";
import withStyles from "isomorphic-style-loader/withStyles";
import styles from "./styles.scss";
import { serviceCaller, ServiceCallerProps } from "../../../../../composition";
import SendingDomainsStep, {
  sendingDomainsSubmissionSchema,
} from "../../../onboardingSteps/domainConfiguration/sendingDomainsStep";
import {
  fetchSendingEmails,
  SendingEmail,
} from "../../../../../../utils/fetch";
import { saveEmailSetupData } from "../../../../../../utils/firebase/emailSetup";
import LoadingSpinner from "../../../../../../toolkit/loadingSpinner";
import ErrorDisplay from "../../../../../../toolkit/errorDisplay";
import SnackbarNotification from "../../../../../../toolkit/snackbarNotification";
import { useSnackbar } from "../../../../../../hooks/useSnackbar";
import WizardBreadcrumb from "../../../onboardingSteps/common/WizardBreadcrumb";

interface Props extends ServiceCallerProps {
  charityId: string;
  onExit?: () => void;
}

const sendingEmailsSchema = yup.object().shape({
  ...sendingDomainsSubmissionSchema.fields,
  tempSenderName: yup.string(),
  tempSenderEmail: yup.string(),
});

const SendingEmailsMaintainPage: React.FC<Props> = ({
  charityId,
  config,
  serviceAccessToken,
  onExit,
}) => {
  const { snackbarProps, showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [existingSendingEmails, setExistingSendingEmails] = useState<
    SendingEmail[]
  >([]);

  const methods = useForm({
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: yupResolver(sendingEmailsSchema),
    defaultValues: {
      sendingDomains: [],
      tempSenderName: "",
      tempSenderEmail: "",
    },
  });

  useEffect(() => {
    if (!charityId || !config?.adminServiceUrl || !serviceAccessToken) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    fetchSendingEmails(config.adminServiceUrl, charityId, serviceAccessToken)
      .then(async emails => {
        if (cancelled) {
          return;
        }
        setExistingSendingEmails(emails);
        if (emails.length > 0) {
          await saveEmailSetupData(charityId, {
            sendingEmailsConfigured: true,
          });
        }
        setError(null);
      })
      .catch(err => {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : "Failed to load sending emails"
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [charityId, config?.adminServiceUrl, serviceAccessToken]);

  return (
    <>
      <Container className={styles.page}>
        <Row>
          <Col>
            <WizardBreadcrumb onBack={onExit} showBreadcrumb />
            {loading ? (
              <div className="mt-5">
                <LoadingSpinner
                  title="Loading sending addresses"
                  message="Please wait while we load your sending email addresses."
                />
              </div>
            ) : error ? (
              <ErrorDisplay
                errorMessage={error}
                title="Sending addresses"
                showBack={!!onExit}
                onBack={onExit}
              />
            ) : (
              <FormProvider {...methods}>
                <Wizard>
                  <SendingDomainsStep
                    charityId={charityId}
                    existingSendingEmails={existingSendingEmails}
                    onExit={onExit}
                    config={config}
                    serviceAccessToken={serviceAccessToken}
                    showSnackbar={showSnackbar}
                    finishButtonText="Save changes"
                    hideBackButton
                    footerAfterAddForm
                    successMessage="Sending Addresses Updated Successfully"
                  />
                </Wizard>
              </FormProvider>
            )}
          </Col>
        </Row>
      </Container>
      <SnackbarNotification
        {...snackbarProps}
        position={{ vertical: "top", horizontal: "center" }}
      />
    </>
  );
};

const mapStateToProps = (state: any) => ({
  charityId: state.user?.currentCharity?.id || "",
});

export default connect(mapStateToProps)(
  serviceCaller(withStyles(styles)(SendingEmailsMaintainPage))
);
